<script lang="ts">
  /**
   * Full-screen webcam capture for the student profile photo. Opens the
   * front-facing camera via getUserMedia, lets the student take a snapshot,
   * review it (retry or accept), and on accept renders BOTH profile renditions
   * (256x256 thumb + high-res 16:9 full) via `renderProfileRenditions` before
   * emitting them upward. Stacks ABOVE the parent ProfileImagePrompt overlay
   * (z-index 60).
   *
   * Camera tracks are stopped on accept, cancel and onDestroy so the device
   * light never lingers; the review preview object URL is revoked alongside.
   *
   * Emits:
   *   capture { detail: ProfileRenditions }  — thumb + full JPEG data URLs
   *   close                                   — student cancelled
   */
  import { createEventDispatcher, onDestroy, onMount } from "svelte";
  import { _ } from "./i18n";
  import { renderProfileRenditions, type ProfileRenditions } from "./image";

  const dispatch = createEventDispatcher<{
    capture: ProfileRenditions;
    close: void;
  }>();

  let video: HTMLVideoElement | undefined;
  let canvas: HTMLCanvasElement | undefined;
  let stream: MediaStream | null = null;
  let phase: "live" | "review" = "live";
  let snapshotBlob: Blob | null = null;
  let snapshotUrl: string | null = null;
  let error: string | null = null;
  let busy = false;

  async function start(): Promise<void> {
    error = null;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: "user",
          width: { ideal: 1920 },
          height: { ideal: 1080 },
        },
        audio: false,
      });
      if (video) {
        video.srcObject = stream;
        await video.play();
      }
      phase = "live";
    } catch {
      error = $_("webcam.error");
    }
  }

  function snapshot(): void {
    if (!video || !canvas || !stream) return;
    const w = video.videoWidth;
    const h = video.videoHeight;
    if (w === 0 || h === 0) return;
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(video, 0, 0, w, h);
    canvas.toBlob(
      (blob) => {
        if (!blob) return;
        if (snapshotUrl) URL.revokeObjectURL(snapshotUrl);
        snapshotBlob = blob;
        snapshotUrl = URL.createObjectURL(blob);
        phase = "review";
      },
      "image/jpeg",
      0.92,
    );
  }

  function retry(): void {
    if (snapshotUrl) URL.revokeObjectURL(snapshotUrl);
    snapshotUrl = null;
    snapshotBlob = null;
    phase = "live";
  }

  async function accept(): Promise<void> {
    if (!snapshotBlob || busy) return;
    busy = true;
    try {
      const renditions = await renderProfileRenditions(snapshotBlob);
      dispatch("capture", renditions);
      stop();
    } catch {
      error = $_("webcam.error");
    } finally {
      busy = false;
    }
  }

  function stop(): void {
    if (stream) {
      for (const tr of stream.getTracks()) tr.stop();
      stream = null;
    }
    if (snapshotUrl) {
      URL.revokeObjectURL(snapshotUrl);
      snapshotUrl = null;
    }
    if (video) video.srcObject = null;
  }

  function cancel(): void {
    stop();
    dispatch("close");
  }

  onMount(start);
  onDestroy(stop);
</script>

<div class="overlay" role="dialog" aria-modal="true">
  <div class="card">
    <h2 class="title">{$_("webcam.title")}</h2>

    <canvas bind:this={canvas} hidden></canvas>

    {#if error}
      <p class="error" data-testid="webcam-error">{error}</p>
      <div class="actions">
        <button
          type="button"
          class="btn ghost"
          data-testid="webcam-cancel"
          on:click={cancel}
        >
          {$_("webcam.cancel")}
        </button>
      </div>
    {:else}
      <!-- Video element stays in the DOM whenever !error so `bind:this` is set
           before start() calls play(); we toggle visibility via phase. -->
      <video
        bind:this={video}
        playsinline
        muted
        data-testid="webcam-video"
        style:display={phase === "live" ? "block" : "none"}
      ></video>

      {#if phase === "review"}
        <img
          class="shot"
          src={snapshotUrl}
          alt={$_("webcam.title")}
          data-testid="webcam-shot"
        />
      {/if}

      {#if phase === "live"}
        <div class="actions">
          <button
            type="button"
            class="btn primary"
            data-testid="webcam-snapshot"
            on:click={snapshot}
          >
            {$_("webcam.snapshot")}
          </button>
          <button
            type="button"
            class="btn ghost"
            data-testid="webcam-cancel"
            on:click={cancel}
          >
            {$_("webcam.cancel")}
          </button>
        </div>
      {:else}
        <div class="actions">
          <button
            type="button"
            class="btn ghost"
            data-testid="webcam-retry"
            on:click={retry}
          >
            {$_("webcam.retry")}
          </button>
          <button
            type="button"
            class="btn primary"
            data-testid="webcam-accept"
            disabled={busy}
            on:click={accept}
          >
            {$_("webcam.use")}
          </button>
        </div>
      {/if}
    {/if}
  </div>
</div>

<style>
  .overlay {
    position: fixed;
    inset: 0;
    display: grid;
    place-items: center;
    background: rgba(15, 23, 42, 0.7);
    z-index: 60;
    padding: 1rem;
    /* Full-screen overlay under the iOS status bar / home indicator; keep the
       card's title and action buttons tappable. env() is 0 elsewhere. */
    padding-top: max(1rem, env(safe-area-inset-top));
    padding-bottom: max(1rem, env(safe-area-inset-bottom));
  }
  .card {
    width: min(26rem, 100%);
    background: #1f2937;
    color: #f3f4f6;
    border-radius: 1rem;
    padding: 1.5rem;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.4);
    text-align: center;
  }
  .title {
    margin: 0 0 1rem;
    font-size: 1.25rem;
  }
  video,
  .shot {
    width: 100%;
    max-width: 320px;
    aspect-ratio: 1 / 1;
    object-fit: cover;
    border-radius: 0.75rem;
    background: #000;
    margin: 0 auto 1rem;
    display: block;
  }
  .error {
    color: #fca5a5;
    margin: 0 0 1rem;
    font-size: 0.9rem;
  }
  .actions {
    display: flex;
    gap: 0.5rem;
    justify-content: center;
    flex-wrap: wrap;
  }
  .btn {
    appearance: none;
    border: 1px solid rgba(255, 255, 255, 0.18);
    background: #374151;
    color: #f3f4f6;
    border-radius: 0.6rem;
    padding: 0.6rem 1rem;
    font-size: 0.95rem;
    cursor: pointer;
  }
  .btn.ghost {
    background: transparent;
  }
  .btn.primary {
    background: #2563eb;
    border-color: #2563eb;
  }
  .btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
</style>
